// The client-side contract of the brand's prize wheel ("Szerencsekerék", the
// second game on the Challenges / Játékok page). Everything the page assumes
// about the API lives here, so it can be re-aligned with the API in one place.
// Mirrors giftDropShape.js.
//
// Contract (wheel3d/WHEEL_CONTRACT.md v1.1 "API — FINAL wire contract"):
//   Auth like the gift drops: requireAuthOrShopify + requireBrandPermission('manageCampaigns').
//   GET   /v3/brands/:brandId/prize-wheels?status=&limit=1..100&before=(ISO|ms)
//         → { serverTime, serverNowMs, brandId, environment, activeExists, nextBefore, prizeWheels } newest first
//   POST  /v3/brands/:brandId/prize-wheels { title, prizeDescription, winnerCount, opensAt?, drawAt, rulesAccepted: true }
//         → 201 { serverTime, serverNowMs, prizeWheel }
//         409 prize_wheel_active_exists { wheelId } — one scheduled|drawing wheel per brand
//   PATCH /v3/brands/:brandId/prize-wheels/:wheelId { title?, prizeDescription?, drawAt? } → { …, prizeWheel }
//         only while scheduled AND nobody joined AND drawAt > now + 10 min (prizeWheel.editable) else 409 prize_wheel_locked
//   POST  /v3/brands/:brandId/prize-wheels/:wheelId/cancel → { …, prizeWheel } (only while scheduled, else 409 prize_wheel_not_cancellable)
//   GET   /v3/brands/:brandId/prize-wheels/:wheelId/winners
//         → { serverTime, serverNowMs, wheelId, status, participantCount, seedHash, seed, winners }
//   Validation: title 1..80, prizeDescription 1..500 (trimmed), winnerCount 1..10, opensAt default now,
//               drawAt ≥ opensAt + 10 min and ≤ now + 30 days, rulesAccepted === true.
//   Envelope: as the gift drops (api() unwraps `message`; errors carry the code in `message`).
//   PrizeWheel: id, brandId, environment, brand { id, name, logoUrl, brandColor }, title, prizeDescription,
//     winnerCount, opensAt(Ms), drawAt(Ms), status scheduled|drawing|finished|cancelled, joinOpen,
//     participantCount, seedHash, seed (finished only), finishedAt(Ms), visibleUntil(Ms), createdAt(Ms),
//     cancelledAt(Ms), editable, spin { startAt(Ms), endsAt(Ms), roundDurationMs, gapMs, rounds } | null,
//     winners [{ round, entryIndex, user { id, username, displayName, avatarUrl, anonymous } }] | null.
//   Socket: /style-universe, the gift:brand:<brandId> room (giftDropsSocket.js) — PRIZE_WHEEL_EVENTS.

import { toMs, errorCodeOf } from './giftDropShape';
import { normalizeProduct } from './wheel/wheelProducts';
import { normalizeSpin as normalizeLiveSpin } from './wheel/wheelSpin';

// ── v2 (WHEEL_CONTRACT.md "v2 — CEO DECISIONS"; wins over the above) ──
//   Prizes are the brand's PRODUCTS: `rounds: [{ round, productIds }]` on create / PATCH, 1..10 rounds,
//   each round = one spin = one winner who gets ALL its products (qty 1, free + mandatory free shipping —
//   the brand pays the shipping). winnerCount = rounds.length; prizeDescription optional.
//   `excludePreviousWinners` (default true) and `eligibility`: followers_at_schedule | followers | everyone.
//   New status `ready` between scheduled and drawing: drawAt passed, the BRAND starts the draw over the
//   socket (DRAW_NOW_EVENT in api/prizeWheels.js); the server draws by itself at autoDrawAt (drawAt + 30 s).
//   Wheel payload (assumed until the API finalises it): rounds [{ round, products: [{ id, name, imageUrl,
//   price, currency }] }], eligibility, excludePreviousWinners, autoDrawAt(Ms).

export const PATHS = {
  list: (brandId) => `/brands/${encodeURIComponent(brandId)}/prize-wheels`,
  create: (brandId) => `/brands/${encodeURIComponent(brandId)}/prize-wheels`,
  one: (brandId, id) => `/brands/${encodeURIComponent(brandId)}/prize-wheels/${encodeURIComponent(id)}`,
  /** REST fallback of the socket draw-now (the Shopify embedded app has no socket). Assumed path. */
  drawNow: (brandId, id) =>
    `/brands/${encodeURIComponent(brandId)}/prize-wheels/${encodeURIComponent(id)}/draw`,
  cancel: (brandId, id) =>
    `/brands/${encodeURIComponent(brandId)}/prize-wheels/${encodeURIComponent(id)}/cancel`,
  winners: (brandId, id) =>
    `/brands/${encodeURIComponent(brandId)}/prize-wheels/${encodeURIComponent(id)}/winners`,
};

export const PRIZE_WHEEL_EVENTS = [
  'prize_wheels_state',
  'prize_wheel_scheduled',
  'prize_wheel_updated',
  'prize_wheel_count',
  'prize_wheel_spin',
  'prize_wheel_finished',
  'prize_wheel_cancelled',
  'prize_wheel_ready',
];

export const TITLE_MAX = 80;
export const PRIZE_MAX = 500;
export const WINNERS_MIN = 1;
export const WINNERS_MAX = 10;
/** drawAt ≥ opensAt + 10 min */
export const MIN_LEAD_MS = 10 * 60 * 1000;
/** drawAt ≤ now + 30 days */
export const MAX_AHEAD_MS = 30 * 24 * 60 * 60 * 1000;
export const HISTORY_PAGE_SIZE = 20;
/** finished wheels stay on the map this long (showResultsHours) when the API sends no visibleUntil */
export const SHOW_RESULTS_MS = 24 * 60 * 60 * 1000;

/** Polling cadence while the socket is down. */
export const POLL_DRAWING_MS = 3000;
export const POLL_SCHEDULED_MS = 10000;
export const POLL_IDLE_MS = 30000;

export const STATUSES = ['scheduled', 'ready', 'drawing', 'finished', 'cancelled'];
export const ACTIVE_STATUSES = ['scheduled', 'ready', 'drawing'];
export const ROUNDS_MAX = 10;
/** Distinct products per round (contract v2.2). */
export const ROUND_PRODUCTS_MAX = 5;
export const ELIGIBILITY = ['followers_at_schedule', 'followers', 'everyone'];
export const DEFAULT_ELIGIBILITY = 'everyone';
/** The brand has this long after drawAt to press "start"; then the server draws by itself. */
export const AUTO_DRAW_MS = 30000;
export const POLL_READY_MS = 2000;

/** First of `${key}Ms` / `key` / snake_case that parses as a time. */
function timeField(raw, key) {
  const snake = key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
  for (const k of [`${key}Ms`, key, `${snake}_ms`, snake]) {
    const ms = toMs(raw?.[k]);
    if (ms !== null) return ms;
  }
  return null;
}

const finiteOrNull = (v) => (v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null);
const strOrNull = (v) => (v === null || v === undefined || v === '' ? null : String(v));
const boolOrNull = (v) => (typeof v === 'boolean' ? v : null);

/**
 * @typedef {object} WheelWinner
 * @property {number|null} round
 * @property {number|null} entryIndex
 * @property {string} name        display name or username ('' when hidden)
 * @property {string|null} username
 * @property {string|null} avatarUrl
 * @property {boolean} anonymous  private profile / junior
 */

/** @returns {WheelWinner|null} */
export function normalizeWheelWinner(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const u = raw.user && typeof raw.user === 'object' ? raw.user : raw;
  const anonymous = Boolean(u.anonymous ?? raw.anonymous ?? false);
  const username = anonymous ? null : strOrNull(u.username);
  return {
    round: finiteOrNull(raw.round),
    entryIndex: finiteOrNull(raw.entryIndex ?? raw.entry_index ?? raw.winnerIndex),
    name: anonymous ? '' : String(u.displayName ?? u.display_name ?? u.name ?? username ?? ''),
    username,
    avatarUrl: anonymous ? null : strOrNull(u.avatarUrl ?? u.avatar_url),
    anonymous,
  };
}

const winnersOf = (list) =>
  Array.isArray(list)
    ? list.map(normalizeWheelWinner).filter(Boolean).sort((a, b) => (a.round ?? 0) - (b.round ?? 0))
    : null;

function normalizeSpin(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    startAt: timeField(raw, 'startAt'),
    endsAt: timeField(raw, 'endsAt'),
    roundDurationMs: finiteOrNull(raw.roundDurationMs),
    gapMs: finiteOrNull(raw.gapMs),
    rounds: Array.isArray(raw.rounds) ? raw.rounds.length : 0,
  };
}

/** v2.2: `rounds[].winner` (finished only) → the winners list, when `winners` itself is absent. */
function roundWinnersOf(list) {
  if (!Array.isArray(list)) return null;
  const out = list
    .map((r, i) => (r?.winner ? normalizeWheelWinner({ ...(r.winner.user ? r.winner : { user: r.winner, entryIndex: r.winner.entryIndex }), round: r.round ?? i + 1 }) : null))
    .filter(Boolean);
  return out.length ? out : null;
}

/** `rounds: [{ round, products | productIds }]` → [{ round, products, productIds }] in round order. */
function roundsOf(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((r, i) => {
      const products = Array.isArray(r?.products) ? r.products.map(normalizeProduct).filter(Boolean) : [];
      const ids = Array.isArray(r?.productIds) ? r.productIds.map(String) : products.map((p) => p.id);
      return { round: finiteOrNull(r?.round) ?? i + 1, products, productIds: ids };
    })
    .sort((a, b) => a.round - b.round);
}

/**
 * @typedef {object} PrizeWheel
 * @property {string} id
 * @property {string} status       scheduled | drawing | finished | cancelled (or whatever the API sent)
 * @property {string} title
 * @property {string} prizeDescription
 * @property {number} winnerCount
 * @property {number|null} opensAt  epoch ms (server clock)
 * @property {number|null} drawAt
 * @property {number} participantCount
 * @property {string|null} seedHash
 * @property {string|null} seed     revealed once finished
 * @property {boolean|null} editable  the API's word on PATCH; null = not sent
 * @property {WheelWinner[]|null} winners
 */

/** @returns {PrizeWheel|null} */
export function normalizeWheel(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id ?? raw.wheelId ?? raw.wheel_id;
  if (id === undefined || id === null) return null;
  const b = raw.brand && typeof raw.brand === 'object' ? raw.brand : null;
  return {
    id: String(id),
    brandId: strOrNull(raw.brandId ?? raw.brand_id ?? b?.id),
    environment: raw.environment ?? null,
    brand: b
      ? {
          id: strOrNull(b.id),
          name: strOrNull(b.name),
          logoUrl: strOrNull(b.logoUrl ?? b.logo_url),
          brandColor: strOrNull(b.brandColor ?? b.brand_color),
        }
      : null,
    title: String(raw.title ?? ''),
    prizeDescription: String(raw.prizeDescription ?? raw.prize_description ?? ''),
    winnerCount: finiteOrNull(raw.winnerCount ?? raw.winner_count) ?? (Array.isArray(raw.rounds) && raw.rounds.length ? raw.rounds.length : 1),
    rounds: roundsOf(raw.rounds),
    eligibility: ELIGIBILITY.includes(raw.eligibility) ? raw.eligibility : null,
    excludePreviousWinners: typeof raw.excludePreviousWinners === 'boolean' ? raw.excludePreviousWinners : true,
    opensAt: timeField(raw, 'opensAt'),
    drawAt: timeField(raw, 'drawAt'),
    status: String(raw.status ?? 'scheduled'),
    joinOpen: boolOrNull(raw.joinOpen),
    participantCount: finiteOrNull(raw.participantCount ?? raw.participant_count) ?? 0,
    seedHash: strOrNull(raw.seedHash ?? raw.seed_hash),
    seed: strOrNull(raw.seed),
    createdAt: timeField(raw, 'createdAt'),
    cancelledAt: timeField(raw, 'cancelledAt'),
    finishedAt: timeField(raw, 'finishedAt'),
    visibleUntil: timeField(raw, 'visibleUntil'),
    editable: boolOrNull(raw.editable ?? raw.canEdit),
    spin: normalizeSpin(raw.spin),
    // the full spin for the live view (null when it cannot be played)
    liveSpin: normalizeLiveSpin(raw.spin, String(id)),
    autoDrawAt: timeField(raw, 'autoDrawAt') ?? (timeField(raw, 'drawAt') !== null ? timeField(raw, 'drawAt') + AUTO_DRAW_MS : null),
    winners: winnersOf(raw.winners) ?? roundWinnersOf(raw.rounds),
    drawable: boolOrNull(raw.drawable),
  };
}

const sortKey = (w) => w.createdAt ?? w.drawAt ?? 0;
export function sortNewestFirst(wheels) {
  return [...wheels].sort((a, b) => sortKey(b) - sortKey(a));
}

/** The GET list payload (already unwrapped from the envelope). Tolerates a bare array. */
export function normalizeWheelList(payload) {
  const body = Array.isArray(payload) ? { prizeWheels: payload } : (payload ?? {});
  const rows = body.prizeWheels ?? body.wheels ?? body.items ?? body.rows;
  const wheels = sortNewestFirst(Array.isArray(rows) ? rows.map(normalizeWheel).filter(Boolean) : []);
  return {
    wheels,
    nextBefore: body.nextBefore ?? body.next_before ?? null,
    activeExists: boolOrNull(body.activeExists),
    serverNowMs: serverNowOf(body),
  };
}

export function serverNowOf(body) {
  return toMs(body?.serverNowMs ?? body?.server_now_ms) ?? toMs(body?.serverTime);
}

/** A create / update / cancel payload: `{ prizeWheel, serverNowMs }` or the wheel itself. */
export function wheelFromPayload(payload) {
  return normalizeWheel(payload?.prizeWheel ?? payload?.wheel ?? payload);
}

/** The /winners payload. */
export function normalizeWinnersPayload(payload) {
  const body = payload ?? {};
  return {
    wheelId: strOrNull(body.wheelId),
    status: strOrNull(body.status),
    participantCount: finiteOrNull(body.participantCount),
    seedHash: strOrNull(body.seedHash),
    seed: strOrNull(body.seed),
    winners: winnersOf(body.winners) ?? [],
  };
}

/** The first history page read again, merged into what is shown (older "Load more" rows stay). */
export function mergeFirstPage(shown, fresh) {
  if (!shown.length) return fresh;
  const ids = new Set(fresh.map((w) => w.id));
  const oldest = fresh.length ? sortKey(fresh[fresh.length - 1]) : Infinity;
  const tail = shown.filter((w) => !ids.has(w.id) && sortKey(w) < oldest);
  return [...fresh, ...tail];
}

export const isActive = (w) => !!w && ACTIVE_STATUSES.includes(w.status);

/**
 * The wheel the card shows: the scheduled / drawing one, else the newest
 * finished one still standing on the map (visibleUntil, else finishedAt /
 * drawAt + 24 h). Everything else is history.
 */
export function currentWheel(wheels, nowMs) {
  const active = wheels.find(isActive);
  if (active) return active;
  const done = wheels.find((w) => w.status === 'finished');
  if (!done) return null;
  const until = done.visibleUntil ?? ((done.finishedAt ?? done.drawAt ?? -Infinity) + SHOW_RESULTS_MS);
  return nowMs < until ? done : null;
}

/** PATCH allowed? The API says so in `editable`; without it: scheduled, nobody in, draw > 10 min away. */
export function isEditable(wheel, nowMs) {
  if (!wheel || wheel.status !== 'scheduled') return false;
  if (wheel.editable !== null && wheel.editable !== undefined) return wheel.editable;
  return wheel.participantCount === 0 && (wheel.drawAt ?? 0) > nowMs + MIN_LEAD_MS;
}

/** Why editing is locked (an i18n key), or null when it is not. */
export function lockReasonKey(wheel, nowMs) {
  if (!wheel || wheel.status !== 'scheduled' || isEditable(wheel, nowMs)) return null;
  if (wheel.participantCount > 0) return 'wheel.lock.joined';
  if ((wheel.drawAt ?? 0) <= nowMs + MIN_LEAD_MS) return 'wheel.lock.soon';
  return 'wheel.lock.generic';
}

export const isCancellable = (wheel) => !!wheel && wheel.status === 'scheduled';

/**
 * Where a wheel is at `nowMs` (server time):
 *   opensSoon  scheduled, joining not open yet   remainingMs → opensAt
 *   open       scheduled, joining open           remainingMs → drawAt
 *   drawing    the spin is playing in the app    remainingMs → spin end
 *   finished / cancelled                         remainingMs 0
 */
export function wheelPhase(wheel, nowMs) {
  const s = wheel.status;
  // drawAt passed: waiting for the brand's start (≤ 30 s) — also before the server says `ready`.
  if (s === 'ready' || (s === 'scheduled' && (wheel.drawable === true || (wheel.drawAt !== null && nowMs >= wheel.drawAt)))) {
    const auto = wheel.autoDrawAt ?? (wheel.drawAt ?? nowMs) + AUTO_DRAW_MS;
    return { phase: 'ready', remainingMs: Math.max(0, auto - nowMs), toDrawMs: 0 };
  }
  if (s === 'scheduled') {
    if (wheel.opensAt !== null && nowMs < wheel.opensAt) {
      return { phase: 'opensSoon', remainingMs: wheel.opensAt - nowMs, toDrawMs: Math.max(0, (wheel.drawAt ?? nowMs) - nowMs) };
    }
    const toDraw = Math.max(0, (wheel.drawAt ?? nowMs) - nowMs);
    return { phase: 'open', remainingMs: toDraw, toDrawMs: toDraw };
  }
  if (s === 'drawing') {
    const end = wheel.spin?.endsAt ?? null;
    return { phase: 'drawing', remainingMs: end !== null ? Math.max(0, end - nowMs) : 0, toDrawMs: 0 };
  }
  return { phase: STATUSES.includes(s) ? s : 'other', remainingMs: 0, toDrawMs: 0 };
}

/** 2 d 03:04:05 → { days: 2, hms: '03:04:05' } (rounded up to the second). */
export function splitCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const days = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return { days, hms: `${pad(h)}:${pad(m)}:${pad(s)}` };
}

/** The next poll: fast while the spin plays, sooner while scheduled. */
export function pollIntervalMs(wheel) {
  if (wheel?.status === 'drawing') return POLL_DRAWING_MS;
  if (wheel?.status === 'ready') return POLL_READY_MS;
  if (wheel?.status === 'scheduled') return POLL_SCHEDULED_MS;
  return POLL_IDLE_MS;
}

/** When to read again by itself: just after the draw time, or the spin's end. */
export function nextRecheckAt(wheel) {
  if (!wheel) return null;
  if (wheel.status === 'scheduled') return wheel.drawAt;
  if (wheel.status === 'ready') return wheel.autoDrawAt;
  if (wheel.status === 'drawing') return wheel.spin?.endsAt ?? null;
  return null;
}

export function shortHash(h, head = 10, tail = 6) {
  if (!h) return '';
  return h.length <= head + tail + 1 ? h : `${h.slice(0, head)}…${h.slice(-tail)}`;
}

// ---- Form: local date / time inputs ↔ epoch ms ----

const pad2 = (n) => String(n).padStart(2, '0');
/** epoch ms → 'YYYY-MM-DD' in local time */
export function toDateInput(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
/** epoch ms → 'HH:mm' in local time */
export function toTimeInput(ms) {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
/** ('YYYY-MM-DD', 'HH:mm') local → epoch ms; null when either is missing / invalid. */
export function fromDateTimeInputs(date, time) {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  const tm = /^(\d{1,2}):(\d{2})/.exec(time || '');
  if (!dm || !tm) return null;
  const d = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]), 0, 0);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** The default draw time: tomorrow at 20:00 local. */
export function defaultDrawAt(nowMs) {
  const d = new Date(nowMs);
  d.setDate(d.getDate() + 1);
  d.setHours(20, 0, 0, 0);
  return d.getTime();
}

/** Quick picks for the draw time, relative to the opening. */
export function quickDrawTimes(opensAtMs) {
  const inHour = Math.ceil((opensAtMs + 60 * 60 * 1000) / (5 * 60 * 1000)) * (5 * 60 * 1000);
  const tomorrow = defaultDrawAt(opensAtMs);
  const week = new Date(opensAtMs);
  week.setDate(week.getDate() + 7);
  week.setHours(20, 0, 0, 0);
  return [
    { key: 'inHour', ms: inHour },
    { key: 'tomorrow', ms: tomorrow },
    { key: 'week', ms: week.getTime() },
  ];
}

const sameIds = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);
const roundsBody = (rounds) => rounds.map((r) => ({ productIds: [...new Set(r.productIds.map(String))] }));

/**
 * Checks the form the way the API will. `values`:
 *   { title, prizeDescription, rounds: [{ productIds }], excludePreviousWinners, eligibility,
 *     opensNow, opensAt (ms|null), drawAt (ms|null), rulesAccepted }
 * `mode` 'create' | 'edit' (edit: no opensAt / rules; sends only what changed).
 * → { errors: { field: i18nKey }, body } — body is what to send when errors is empty.
 */
export function validateWheelForm(values, nowMs, { mode = 'create', original = null } = {}) {
  const errors = {};
  const title = String(values.title ?? '').trim();
  const prize = String(values.prizeDescription ?? '').trim();
  if (!title) errors.title = 'wheel.form.err.titleRequired';
  else if (title.length > TITLE_MAX) errors.title = 'wheel.form.err.titleTooLong';
  if (prize.length > PRIZE_MAX) errors.prizeDescription = 'wheel.form.err.prizeTooLong';

  const rounds = Array.isArray(values.rounds) ? values.rounds : [];
  if (rounds.length < WINNERS_MIN || rounds.length > ROUNDS_MAX) errors.rounds = 'wheel.form.err.roundsCount';
  else if (rounds.some((r) => !r.productIds || r.productIds.length === 0)) errors.rounds = 'wheel.form.err.roundEmpty';
  else if (rounds.some((r) => new Set(r.productIds).size > ROUND_PRODUCTS_MAX)) errors.rounds = 'wheel.form.err.roundTooMany';

  const eligibility = ELIGIBILITY.includes(values.eligibility) ? values.eligibility : null;
  if (!eligibility) errors.eligibility = 'wheel.form.err.eligibility';
  const exclude = values.excludePreviousWinners !== false;

  let opensAt = nowMs;
  if (mode === 'edit') opensAt = Math.max(original?.opensAt ?? nowMs, nowMs);
  else if (!values.opensNow) {
    if (values.opensAt === null || values.opensAt === undefined) errors.opensAt = 'wheel.form.err.opensRequired';
    else opensAt = Math.max(values.opensAt, nowMs); // a past opening → now (as the API does)
  }

  const drawAt = values.drawAt;
  if (drawAt === null || drawAt === undefined) errors.drawAt = 'wheel.form.err.drawRequired';
  else if (drawAt < opensAt + MIN_LEAD_MS) errors.drawAt = 'wheel.form.err.drawTooSoon';
  else if (drawAt > nowMs + MAX_AHEAD_MS) errors.drawAt = 'wheel.form.err.drawTooLate';

  if (mode === 'create' && values.rulesAccepted !== true) errors.rulesAccepted = 'wheel.form.err.rules';
  if (mode === 'create' && values.freeShippingAccepted !== true) errors.freeShippingAccepted = 'wheel.form.err.freeShipping';

  let body;
  if (mode === 'edit') {
    body = {};
    if (!original || title !== original.title) body.title = title;
    if (!original || prize !== original.prizeDescription) body.prizeDescription = prize || null;
    if (drawAt != null && (!original || drawAt !== original.drawAt)) body.drawAt = new Date(drawAt).toISOString();
    const before = original?.rounds ?? [];
    const changed = !original || before.length !== rounds.length
      || rounds.some((r, i) => !sameIds(r.productIds.map(String), before[i].productIds));
    if (changed) body.rounds = roundsBody(rounds);
    if (!original || exclude !== original.excludePreviousWinners) body.excludePreviousWinners = exclude;
    if (!original || eligibility !== original.eligibility) body.eligibility = eligibility;
  } else {
    body = {
      title,
      rounds: roundsBody(rounds),
      winnerCount: rounds.length,
      excludePreviousWinners: exclude,
      eligibility,
      drawAt: drawAt != null ? new Date(drawAt).toISOString() : null,
      rulesAccepted: values.rulesAccepted === true,
      freeShippingAccepted: values.freeShippingAccepted === true,
    };
    if (prize) body.prizeDescription = prize;
    if (!values.opensNow && values.opensAt != null) body.opensAt = new Date(opensAt).toISOString();
  }
  return { errors, body };
}

// ---- Live events (the gift:brand:<brandId> socket room) ----

/**
 * Applies a socket event to the shown wheels (newest first). The page reads
 * the list again after every event anyway — the server stays the source of
 * truth; this only lets it update at once.
 */
export function applyWheelEvent(wheels, name, data) {
  if (!PRIZE_WHEEL_EVENTS.includes(name) || !data) return wheels;
  if (name === 'prize_wheels_state') {
    const live = Array.isArray(data.prizeWheels) ? data.prizeWheels.map(normalizeWheel).filter(Boolean) : [];
    const byId = new Map(wheels.map((w) => [w.id, w]));
    for (const w of live) byId.set(w.id, { ...byId.get(w.id), ...w });
    return sortNewestFirst([...byId.values()]);
  }
  if (name === 'prize_wheel_count') {
    const id = strOrNull(data.wheelId);
    const n = finiteOrNull(data.participantCount);
    if (!id || n === null) return wheels;
    return wheels.map((w) => (w.id === id ? { ...w, participantCount: n } : w));
  }
  const full = normalizeWheel(data.prizeWheel);
  if (full) {
    const prev = wheels.find((w) => w.id === full.id);
    const merged = prev ? { ...prev, ...full, createdAt: full.createdAt ?? prev.createdAt } : full;
    if (name === 'prize_wheel_finished' && data.seed) merged.seed = String(data.seed);
    if (name === 'prize_wheel_finished' && Array.isArray(data.winners)) merged.winners = winnersOf(data.winners);
    return sortNewestFirst([merged, ...wheels.filter((w) => w.id !== full.id)]);
  }
  const id = strOrNull(data.wheelId);
  if (!id) return wheels;
  const status = { prize_wheel_cancelled: 'cancelled', prize_wheel_ready: 'ready', prize_wheel_spin: 'drawing', prize_wheel_finished: 'finished' }[name];
  if (!status) return wheels;
  return wheels.map((w) => {
    if (w.id !== id) return w;
    const next = { ...w, status };
    if (name === 'prize_wheel_finished') {
      if (data.seed) next.seed = String(data.seed);
      if (Array.isArray(data.winners)) next.winners = winnersOf(data.winners);
    }
    if (name === 'prize_wheel_spin' && data.spin) {
      next.spin = normalizeSpin(data.spin);
      next.liveSpin = normalizeLiveSpin(data.spin, id);
    }
    return next;
  });
}

// ---- Errors → one readable line ----

const ERROR_KEYS = {
  prize_wheel_active_exists: 'wheel.err.activeExists',
  prize_wheel_locked: 'wheel.err.locked',
  prize_wheel_not_cancellable: 'wheel.err.notCancellable',
  prize_wheel_not_found: 'wheel.err.notFound',
  prize_wheel_not_ready: 'wheel.err.notReady',
  prize_wheel_already_drawn: 'wheel.err.alreadyDrawn',
  prize_wheel_forbidden: 'wheel.err.forbidden',
  prize_wheel_products_invalid: 'wheel.err.products',
  prize_wheel_product_invalid: 'wheel.err.products',
  socket_unavailable: 'wheel.err.socket',
  ack_timeout: 'wheel.err.socket',
  prize_wheel_invalid: 'wheel.err.invalid',
  prize_wheel_brand_inactive: 'wheel.err.brandInactive',
  prize_wheel_not_eligible: 'wheel.err.notEligible',
  prize_wheels_disabled: 'wheel.err.disabled',
  prize_wheel_disabled: 'wheel.err.disabled',
  FORBIDDEN: 'wheel.err.forbidden',
  INSUFFICIENT_PERMISSIONS: 'wheel.err.forbidden',
  BRAND_NOT_FOUND: 'wheel.err.brandNotFound',
  validation_error: 'wheel.err.invalid',
  forbidden: 'wheel.err.forbidden',
  insufficient_permissions: 'wheel.err.forbidden',
  unauthorized: 'wheel.err.auth',
  invalid_token: 'wheel.err.auth',
  missing_token: 'wheel.err.auth',
  rate_limited: 'wheel.err.rateLimited',
  too_many_requests: 'wheel.err.rateLimited',
};

/** prize_wheel_invalid { field } → the form field it names (title | prizeDescription | …), else null. */
export function invalidFieldOf(err) {
  if (errorCodeOf(err) !== 'prize_wheel_invalid') return null;
  const f = err?.data?.details?.field;
  return typeof f === 'string' ? f : null;
}

/** prize_wheel_invalid { field: 'rounds', reason, round } reasons with their own sentence (contract v2.2). */
export const ROUND_REASONS = ['product_not_found', 'product_inactive', 'product_out_of_stock', 'duplicate_product', 'too_many_products', 'empty_round', 'too_many_rounds'];

/** The i18n key (+ vars) for a thrown error. */
export function errorMessageKey(err) {
  if (!err) return null;
  if (err.name === 'TypeError' && err.status == null) return { key: 'wheel.err.network' };
  const code = errorCodeOf(err);
  const field = invalidFieldOf(err);
  const d = err?.data?.details || {};
  if (field === 'rounds' && ROUND_REASONS.includes(d.reason)) {
    return { key: `wheel.err.rounds.${d.reason}`, vars: { round: d.round ?? '?' } };
  }
  if (field) return { key: 'wheel.err.invalidField', vars: { field } };
  if (code && ERROR_KEYS[code]) return { key: ERROR_KEYS[code] };
  if (err.status === 429) return { key: 'wheel.err.rateLimited' };
  if (err.status === 401) return { key: 'wheel.err.auth' };
  if (err.status === 403) return { key: 'wheel.err.forbidden' };
  // A 404 that names no wheel code: the route itself is missing — the API is not deployed here yet.
  if (err.status === 404) return { key: 'wheel.err.unavailable' };
  if (code) return { key: 'wheel.err.genericCode', vars: { code } };
  return { key: 'wheel.err.generic' };
}

const FIELD_KEYS = {
  title: 'wheel.form.title',
  prizeDescription: 'wheel.form.prize',
  winnerCount: 'wheel.form.rounds',
  rounds: 'wheel.form.rounds',
  eligibility: 'wheel.form.eligibility',
  excludePreviousWinners: 'wheel.form.exclude',
  freeShippingAccepted: 'wheel.free.accept',
  opensAt: 'wheel.form.opens',
  drawAt: 'wheel.form.draw',
  rulesAccepted: 'wheel.form.rules',
};

/** A thrown error → one readable, translated line. */
export function errorText(err, t) {
  const m = errorMessageKey(err);
  if (!m) return '';
  if (m.key === 'wheel.err.invalidField') {
    const label = FIELD_KEYS[m.vars.field] ? t(FIELD_KEYS[m.vars.field]) : m.vars.field;
    return t(m.key, { field: label });
  }
  return t(m.key, m.vars);
}
